import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import emailjs from "@emailjs/browser";
import {
  contactFormSchema,
  ContactFormSchema,
  contactFormDefaultValue,
  getContactFormError,
} from "../../utils/formValidation";
import { customStyles } from "./index.styles";

interface ContactFormProps {
  variant?: "full" | "compact";
  onSubmitSuccess?: () => void;
}

const ContactForm = ({
  variant = "full",
  onSubmitSuccess,
}: ContactFormProps) => {
  const form = useRef<HTMLFormElement | null>(null);
  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleSubmit, getValues, control, formState, reset } =
    useForm<ContactFormSchema>({
      resolver: zodResolver(contactFormSchema),
      mode: "onChange",
      defaultValues: contactFormDefaultValue,
    });

  const resetAlerts = () => {
    setSubmitSuccessful(false);
    setSubmitError(null);
  };

  const onSubmit = async () => {
    const errorMessage = getContactFormError(
      formState.errors,
      getValues().mobile
    );

    if (errorMessage) {
      return;
    }

    setIsSubmitting(true);
    resetAlerts();

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_SERVICE_KEY ?? "",
        process.env.REACT_APP_TEMPLATE ?? "",
        form.current!,
        process.env.REACT_APP_KEY ?? ""
      );
      
      setSubmitSuccessful(true);
      reset();
      setTimeout(resetAlerts, 3000);
      onSubmitSuccess?.();
    } catch (error) {
      console.error("Email sending error:", error);
      setSubmitError(
        "Failed to send message. Please try again or contact us directly."
      );
      setTimeout(resetAlerts, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorMessage = getContactFormError(
    formState.errors,
    getValues().mobile
  );
  const isCompact = variant === "compact";

  return (
    <form ref={form} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={isCompact ? 2 : 3}>
        <Box>
          <Typography sx={customStyles.formLabel}>
            {isCompact ? "Name" : "Full Name *"}
          </Typography>
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                name="name"
                fullWidth
                size="small"
                value={value}
                onChange={onChange}
                placeholder={isCompact ? "" : "Enter your full name"}
              />
            )}
          />
        </Box>

        <Box>
          <Typography sx={customStyles.formLabel}>
            {isCompact ? "Email" : "Email Address *"}
          </Typography>
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                name="email"
                fullWidth
                size="small"
                value={value}
                onChange={onChange}
                placeholder={isCompact ? "" : "your.email@example.com"}
              />
            )}
          />
        </Box>

        <Box>
          <Typography sx={customStyles.formLabel}>
            {isCompact ? "Mobile" : "Mobile Number *"}
          </Typography>
          <Controller
            name="mobile"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                name="mobile"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+61</InputAdornment>
                  ),
                }}
                fullWidth
                size="small"
                value={value}
                onChange={onChange}
                placeholder={isCompact ? "" : "412345678"}
              />
            )}
          />
        </Box>

        <Box>
          <Typography sx={customStyles.formLabel}>
            {isCompact ? "Message" : "Your Message *"}
          </Typography>
          <Controller
            name="message"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                name="message"
                fullWidth
                multiline
                rows={isCompact ? 3 : 5}
                size="small"
                value={value}
                onChange={onChange}
                placeholder={isCompact ? "" : "Tell us how we can help you..."}
              />
            )}
          />
        </Box>

        {errorMessage && (
          <Box>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}

        {submitError && (
          <Box>
            <Alert severity="error">{submitError}</Alert>
          </Box>
        )}

        {!errorMessage && submitSuccessful && (
          <Alert severity="success">
            Thank you for your {isCompact ? "request" : "message"}! We'll get
            back to you soon.
          </Alert>
        )}

        <Button
          variant="contained"
          type="submit"
          size="large"
          disabled={isSubmitting}
          sx={customStyles.submitButton}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? "Sending..." : isCompact ? "Submit" : "Send Message"}
        </Button>
      </Stack>
    </form>
  );
};

export default ContactForm;
